import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CartLine, CartState } from '../../components/models/cart/cart';
import { ProductModel } from '../../components/models/product/product';
import { setProducts } from '../product';

const initialState: CartState = {
    lines: [],
    open: false,
};

/** Never below one, never above what the shop actually has. */
const clampToStock = (quantity: number, stock: number) =>
    Math.max(1, Math.min(Math.round(quantity) || 1, Math.max(stock, 1)));

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        /** Adds to an existing line rather than creating a duplicate one. */
        addToCart: (
            state,
            action: PayloadAction<{ product: ProductModel; quantity?: number }>
        ) => {
            const { product, quantity = 1 } = action.payload;
            if (product.stock <= 0) return;

            const wanted = Math.max(1, Math.round(quantity) || 1);
            const line = state.lines.find((l) => l.product.id === product.id);

            if (line) {
                // Freshest product data wins; the visitor may have opened the
                // detail page since the line was created.
                line.product = product;
                line.quantity = clampToStock(line.quantity + wanted, product.stock);
                return;
            }

            state.lines.push({ product, quantity: clampToStock(wanted, product.stock) });
        },
        setLineQuantity: (
            state,
            action: PayloadAction<{ productId: number; quantity: number }>
        ) => {
            const { productId, quantity } = action.payload;
            const line = state.lines.find((l) => l.product.id === productId);
            if (!line) return;

            if (quantity < 1) {
                state.lines = state.lines.filter((l) => l.product.id !== productId);
                return;
            }

            line.quantity = clampToStock(quantity, line.product.stock);
        },
        removeLine: (state, action: PayloadAction<number>) => {
            state.lines = state.lines.filter((l) => l.product.id !== action.payload);
        },
        clearCart: (state) => {
            state.lines = [];
        },
        openCart: (state) => {
            state.open = true;
        },
        closeCart: (state) => {
            state.open = false;
        },
    },
    extraReducers: (builder) => {
        // Whenever the catalogue lands, re-point every line at the fresh row:
        // prices and stock move while a bag is sitting in localStorage, and a
        // product can be deleted from the admin console outright.
        builder.addCase(setProducts, (state, action) => {
            if (state.lines.length === 0) return;

            state.lines = state.lines.reduce<CartLine[]>((kept, line) => {
                const fresh = action.payload.find((p) => p.id === line.product.id);
                if (!fresh) return kept;

                kept.push({
                    product: fresh,
                    // A sold-out line keeps its quantity so the bag can show it
                    // as unavailable instead of silently changing the order.
                    quantity: fresh.stock > 0 ? clampToStock(line.quantity, fresh.stock) : line.quantity,
                });
                return kept;
            }, []);
        });
    },
});

export const {
    addToCart,
    setLineQuantity,
    removeLine,
    clearCart,
    openCart,
    closeCart,
} = cartSlice.actions;

export default cartSlice.reducer;
